import React from 'react';
import { ArrowRight, CheckCircle, Heart, Users } from 'lucide-react';
import { Button } from './ui/button';
import { Card } from './ui/card';
import { psychologistData } from '../mock';

const HeroSection = () => {
  const scrollToContact = () => {
    const element = document.getElementById('contact');
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <section id="home" className="min-h-screen pt-20">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20">
        <div className="grid lg:grid-cols-2 gap-12 items-center">
          {/* Left Content */}
          <div className="space-y-8">
            <div className="space-y-4">
              <div className="inline-flex items-center space-x-2 bg-sage-green text-white px-4 py-2 rounded-full text-sm font-medium">
                <Heart size={16} />
                <span>Professional Mental Health Care</span>
              </div>
              
              <h1 className="text-4xl lg:text-6xl font-bold text-charcoal-grey leading-tight">
                Reboot Your{' '}
                <span className="text-lavender">Relationship</span>
              </h1>
              
              <p className="text-xl text-charcoal-grey leading-relaxed">
                Feel Heard, Understood & Connected
              </p>
              
              <p className="text-lg text-charcoal-grey leading-relaxed">
                {psychologistData.description}
              </p>
            </div>

            {/* Key Benefits */}
            <div className="space-y-3">
              <div className="flex items-center space-x-3">
                <CheckCircle className="text-sage-green flex-shrink-0" size={20} />
                <span className="text-charcoal-grey">Private, compassionate counseling for all ages</span>
              </div>
              <div className="flex items-center space-x-3">
                <CheckCircle className="text-sage-green flex-shrink-0" size={20} />
                <span className="text-charcoal-grey">First 30-minute online consultation free</span>
              </div>
              <div className="flex items-center space-x-3">
                <CheckCircle className="text-sage-green flex-shrink-0" size={20} />
                <span className="text-charcoal-grey">In-person & online sessions available</span>
              </div>
            </div>

            {/* CTA Buttons */}
            <div className="flex flex-col sm:flex-row gap-4">
              <Button 
                onClick={scrollToContact}
                size="lg"
                className="bg-sage-green hover:bg-soft-blue text-white px-8 py-3 rounded-full font-semibold transition-all duration-200 hover:shadow-lg hover:scale-105"
              >
                Get Free Consultation
                <ArrowRight className="ml-2" size={20} />
              </Button>
              
              <Button 
                variant="outline"
                size="lg"
                onClick={() => {
                  const element = document.getElementById('services');
                  if (element) element.scrollIntoView({ behavior: 'smooth' });
                }}
                className="border-2 border-soft-blue text-soft-blue hover:bg-soft-blue hover:text-white px-8 py-3 rounded-full font-semibold transition-all duration-200"
              >
                Learn More
              </Button>
            </div>

            {/* Contact Info */}
            <div className="flex items-center space-x-6 pt-4 border-t border-gray-200">
              <div className="text-center">
                <p className="text-2xl font-bold text-gray-900">20+ Years</p>
                <p className="text-sm text-gray-600">Experience</p>
              </div>
              <div className="text-center">
                <p className="text-2xl font-bold text-gray-900">1000+</p>
                <p className="text-sm text-gray-600">Happy Clients</p>
              </div>
              <div className="text-center">
                <p className="text-2xl font-bold text-gray-900">5</p>
                <p className="text-sm text-gray-600">Locations</p>
              </div>
            </div>
          </div>

          {/* Right Content - Professional Image */}
          <div className="relative">
            <div className="relative z-10">
              <Card className="p-8 bg-white/90 backdrop-blur-sm shadow-xl border-0">
                <div className="text-center space-y-6">
                  {/* Professional Photo - Clear Headshot */}
                  <div className="w-48 h-48 mx-auto rounded-full overflow-hidden shadow-lg border-4 border-white">
                    <img 
                      src="https://customer-assets.emergentagent.com/job_mindfulhealth-2/artifacts/vec5ksfz_Manish%20R%20Pacher.jpg"
                      alt="Manish Pacher - Clinical Psychologist & Family Therapist"
                      className="w-full h-full object-cover"
                      style={{
                        objectPosition: 'center 10%'
                      }}
                      onError={(e) => {
                        // Fallback to placeholder if image fails to load
                        e.target.style.display = 'none';
                        e.target.nextSibling.style.display = 'flex';
                      }}
                    />
                    <div className="w-full h-full bg-gradient-to-br from-blue-100 to-green-100 rounded-full flex items-center justify-center" style={{display: 'none'}}>
                      <Users size={60} className="text-blue-600" />
                    </div>
                  </div>
                  
                  <div className="space-y-2">
                    <h3 className="text-2xl font-bold text-gray-900">
                      {psychologistData.name}
                    </h3>
                    <p className="text-lg text-blue-600 font-medium">
                      {psychologistData.title}
                    </p>
                    <p className="text-gray-600">
                      {psychologistData.subtitle}
                    </p>
                  </div>

                  {/* Quick Contact */}
                  <div className="space-y-3 pt-4 border-t border-gray-200">
                    <div className="flex items-center justify-center space-x-2 text-gray-700">
                      <span className="font-medium">Call/WhatsApp:</span>
                      <span className="text-green-600 font-semibold">{psychologistData.contact.phone}</span>
                    </div>
                    <Button 
                      onClick={scrollToContact}
                      variant="outline"
                      className="w-full border-blue-200 text-blue-600 hover:bg-blue-50"
                    >
                      Schedule Appointment
                    </Button>
                  </div>
                </div>
              </Card>
            </div>

            {/* Background Decorative Elements */}
            <div className="absolute top-0 right-0 w-72 h-72 bg-blue-100 rounded-full mix-blend-multiply filter blur-xl opacity-40 animate-pulse"></div>
            <div className="absolute bottom-0 left-0 w-72 h-72 bg-green-100 rounded-full mix-blend-multiply filter blur-xl opacity-40 animate-pulse animation-delay-2000"></div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default HeroSection;